// hooks/useNonUsDefaultValuesUpdater.ts
import { useEffect } from 'react';
import { OnboardingFormFields } from '@Types/index';
import type { Country, CountryApiResponse } from '@Types/country';
import type { IBusinessAddressForm } from '@Pages/happyFlowPages/businessAddress/businessAddressForm/businessAddressSchema';
import type { UseFormResetField } from 'react-hook-form';
import type { IHomeAddressForm } from '@Types/homeAddress';
export type CombinedAddressForm = IHomeAddressForm & Partial<IBusinessAddressForm>;
interface NonUsUpdaterParams {
  isUS: boolean;
  countriesData?: CountryApiResponse;
  customerCountry: string | undefined;
  customerState: string;
  defaultValues: CombinedAddressForm;
  setDefaultValues: (values: CombinedAddressForm) => void;
  resetField: UseFormResetField<CombinedAddressForm>;
}

// This hook updates the default values for the country and state fields in the home address form
// when the selected country is not the US. It fetches the country and state information from the
// provided countries data and sets the default values accordingly. If the country or state is not
// found in the data, it does not update the default values. The hook also resets the form fields
// to the new default values whenever the countries data changes.
export const useNonUsCountryDefaultValues = ({
  isUS,
  countriesData,
  customerCountry,
  customerState,
  defaultValues,
  setDefaultValues,
  resetField,
}: NonUsUpdaterParams) => {
  useEffect(() => {
    if (isUS) return;

    const countryObject = countriesData?.countries.find(
      (item: Country) => item.code === customerCountry
    );
    if (!countryObject) return;

    const enrichedCountryObject = {
      ...countryObject,
      title: countryObject.name,
      value: countryObject.code,
    };

    const nonUsState = enrichedCountryObject.subDivisions.find(
      (subDivision: { code: string; name: string }) => subDivision.code === customerState
    );
    const nonUsStateDropDownItem = {
      title: nonUsState ? nonUsState.name : '',
      value: nonUsState ? nonUsState.code : '',
    };

    setDefaultValues({
      ...defaultValues,
      [OnboardingFormFields.COUNTRY]: enrichedCountryObject,
      [OnboardingFormFields.STATE]: nonUsStateDropDownItem,
    });
    resetField(OnboardingFormFields.COUNTRY, {
      defaultValue: enrichedCountryObject,
    });
    resetField(OnboardingFormFields.STATE, {
      defaultValue: nonUsStateDropDownItem,
    });
  }, [countriesData?.countries]);
};
